import type { FitGrade, NormalizedNotice, NoticeEvaluation } from "../notices/types.js";
import { normalizeComparableText } from "../notices/stable-text.js";
import type { EligibilityResult } from "./eligibility.js";
import type { CompanyProfile } from "./profile.js";

export interface FitComponents {
  eligibility: number;
  technicalRelevance: number;
  threeMonthFeasibility: number;
  fundingSuitability: number;
  marketPotential: number;
  consortiumReadiness: number;
  followOnPotential: number;
}

export interface FitScoreCaps extends FitComponents {}

export interface GradeThresholds {
  excellent: number;
  fit: number;
  conditional: number;
  unlikely: number;
}

export const DEFAULT_FIT_SCORE_CAPS: FitScoreCaps = {
  eligibility: 30,
  technicalRelevance: 25,
  threeMonthFeasibility: 15,
  fundingSuitability: 10,
  marketPotential: 10,
  consortiumReadiness: 5,
  followOnPotential: 5,
};

export const DEFAULT_GRADE_THRESHOLDS: GradeThresholds = {
  excellent: 80,
  fit: 60,
  conditional: 40,
  unlikely: 20,
};

export function fitGradeForScore(score: number, thresholds: GradeThresholds = DEFAULT_GRADE_THRESHOLDS): FitGrade {
  if (!Number.isFinite(score) || score < 0 || score > 100) throw new RangeError("적합도 점수는 0~100이어야 합니다.");
  if (score >= thresholds.excellent) return "매우 적합";
  if (score >= thresholds.fit) return "적합";
  if (score >= thresholds.conditional) return "조건부 적합";
  if (score >= thresholds.unlikely) return "부적합 가능성 높음";
  return "사실상 부적합";
}

function countMatches(text: string, values: readonly string[]): string[] {
  return values.filter((value) => text.includes(normalizeComparableText(value)));
}

function scale(points: number, max: number): number {
  return Math.max(0, Math.min(max, Math.round(points)));
}

export function calculateFitComponents(
  notice: NormalizedNotice,
  profile: CompanyProfile,
  eligibility: EligibilityResult,
  caps: FitScoreCaps = DEFAULT_FIT_SCORE_CAPS,
  keywordExtras: readonly string[] = [],
): FitComponents {
  const text = normalizeComparableText(
    `${notice.title} ${notice.summary} ${notice.eligibilityText} ${notice.evaluationCriteria.join(" ")} ${keywordExtras.join(" ")}`,
  );
  const capabilityMatches = countMatches(text, profile.capabilities);
  const marketMatches = countMatches(text, profile.targetMarkets);
  const eligibilityRatio = { "충족": 1, "조건부 충족": 22 / 30, "미확인": 12 / 30, "불충족": 0 }[eligibility.status];
  const eligibilityPoints = scale(caps.eligibility * eligibilityRatio, caps.eligibility);
  const technicalRelevance = scale(capabilityMatches.length * (caps.technicalRelevance / 5), caps.technicalRelevance);
  const threeMonthFeasibility = notice.status === "마감"
    ? 0
    : scale(
      capabilityMatches.length >= 3
        ? caps.threeMonthFeasibility
        : capabilityMatches.length === 2
          ? caps.threeMonthFeasibility * 0.8
          : capabilityMatches.length === 1
            ? caps.threeMonthFeasibility * 0.53
            : caps.threeMonthFeasibility * 0.2,
      caps.threeMonthFeasibility,
    );
  let fundingSuitability = notice.supportAmount?.amountKrw == null
    ? scale(caps.fundingSuitability * 0.3, caps.fundingSuitability)
    : notice.supportAmount.amountKrw >= 50_000_000
      ? caps.fundingSuitability
      : notice.supportAmount.amountKrw >= 20_000_000
        ? scale(caps.fundingSuitability * 0.8, caps.fundingSuitability)
        : scale(caps.fundingSuitability * 0.6, caps.fundingSuitability);
  if (notice.selfContribution?.unit === "percent" && (notice.selfContribution.amountKrw ?? 0) > 50) {
    fundingSuitability = Math.max(0, fundingSuitability - Math.round(caps.fundingSuitability * 0.4));
  }
  const marketPotential = scale(
    marketMatches.length * (caps.marketPotential / 3) + (/조달|사업화|실증/u.test(text) ? caps.marketPotential * 0.2 : 0),
    caps.marketPotential,
  );
  const consortiumRequired = /컨소시엄|공동수급|수요기관|수요처.*필수/u.test(text);
  const consortiumReadiness = consortiumRequired
    ? (profile.partnerships.length > 0 ? caps.consortiumReadiness : scale(caps.consortiumReadiness * 0.2, caps.consortiumReadiness))
    : caps.consortiumReadiness;
  const followOnPotential = /후속|r&d|연구개발|조달|매출|사업화/u.test(text)
    ? caps.followOnPotential
    : scale(caps.followOnPotential * 0.4, caps.followOnPotential);
  return {
    eligibility: eligibilityPoints,
    technicalRelevance,
    threeMonthFeasibility,
    fundingSuitability,
    marketPotential,
    consortiumReadiness,
    followOnPotential,
  };
}

export function totalFitScore(components: FitComponents): number {
  const score = Object.values(components).reduce((sum, value) => sum + value, 0);
  return Math.max(0, Math.min(100, Math.round(score)));
}

export function evaluateFit(
  notice: NormalizedNotice,
  profile: CompanyProfile,
  eligibility: EligibilityResult,
  evaluatedAt: string,
  options?: {
    caps?: FitScoreCaps;
    thresholds?: GradeThresholds;
    keywordExtras?: readonly string[];
  },
): NoticeEvaluation {
  const caps = options?.caps ?? DEFAULT_FIT_SCORE_CAPS;
  const thresholds = options?.thresholds ?? DEFAULT_GRADE_THRESHOLDS;
  const components = calculateFitComponents(notice, profile, eligibility, caps, options?.keywordExtras);
  const fitScore = totalFitScore(components);
  const matchedCapabilities = countMatches(
    normalizeComparableText(`${notice.title} ${notice.summary}`),
    profile.capabilities,
  );
  const risks = [...eligibility.requiredConfirmations];
  if (notice.supportAmount?.amountKrw == null) risks.push("지원금 규모를 정규화하지 못했습니다.");
  if (notice.status === "마감") risks.push("접수가 마감된 공고입니다.");
  const recommendedAction = eligibility.status === "불충족"
    ? "신청자격 불충족으로 우선 추천에서 제외합니다."
    : fitScore >= thresholds.fit
      ? "원문 자격과 제출서류를 확인한 뒤 제안 검토를 진행합니다."
      : "확인 필요 항목을 보완한 뒤 재평가합니다.";
  return {
    noticeId: notice.id,
    companyProfileVersion: profile.version,
    noticeContentHash: notice.contentHash,
    eligibilityStatus: eligibility.status,
    eligibilityReasons: eligibility.reasons,
    fitScore,
    fitGrade: fitGradeForScore(fitScore, thresholds),
    fitReasons: [
      `자격 ${components.eligibility}/${caps.eligibility}`,
      `기술 연관성 ${components.technicalRelevance}/${caps.technicalRelevance}${matchedCapabilities.length ? ` (${matchedCapabilities.join(", ")})` : ""}`,
      `3개월 제안 가능성 ${components.threeMonthFeasibility}/${caps.threeMonthFeasibility}`,
      `지원조건 ${components.fundingSuitability}/${caps.fundingSuitability}`,
      `사업화 ${components.marketPotential}/${caps.marketPotential}`,
      `협력 준비 ${components.consortiumReadiness}/${caps.consortiumReadiness}`,
      `후속 연계 ${components.followOnPotential}/${caps.followOnPotential}`,
    ],
    risks,
    requiredConfirmations: eligibility.requiredConfirmations,
    recommendedAction,
    evaluationMethod: "rule",
    modelName: null,
    promptVersion: null,
    evaluatedAt,
  };
}

export function isPriorityRecommendation(evaluation: NoticeEvaluation, fitThreshold = DEFAULT_GRADE_THRESHOLDS.fit): boolean {
  return evaluation.eligibilityStatus !== "불충족" && evaluation.fitScore >= fitThreshold;
}
